<script setup lang="ts">
import { EDialog, formatBinding, useShortcutRegistry } from '@erpx/ui'
import { useI18n } from 'vue-i18n'

/**
 * The `?` cheat sheet renders the *live* registry, so it can never drift from
 * the bindings that actually exist — a shortcut that was removed disappears
 * from this list by construction.
 *
 * Descriptions are registered once at mount and cannot re-render themselves on
 * a locale switch (`ui/src/lib/keys.ts` stores a plain string), so this app
 * registers catalogue keys and they are resolved here. A registration from
 * `@erpx/ui` itself carries English prose rather than a key, and `te()` is what
 * tells the two apart — an unknown key is shown exactly as registered rather
 * than rendered as its own name.
 */
const open = defineModel<boolean>('open', { default: false })

const { grouped } = useShortcutRegistry()
const { t, te } = useI18n()

function describe(description: string): string {
  return te(description) ? t(description) : description
}

function groupLabel(group: string): string {
  const key = `shell.shortcuts.groups.${group}`
  return te(key) ? t(key) : group
}
</script>

<template>
  <EDialog
    v-model:open="open"
    :title="$t('shell.shortcuts.title')"
    :description="$t('shell.shortcuts.description')"
    size="md"
  >
    <div v-for="section in grouped" :key="section.group" class="mb-4 last:mb-0">
      <h3 class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        {{ groupLabel(section.group) }}
      </h3>
      <dl class="divide-y divide-subtle">
        <div
          v-for="shortcut in section.shortcuts"
          :key="shortcut.id"
          class="flex items-center justify-between gap-4 py-1.5 text-sm"
        >
          <dt class="text-secondary">{{ describe(shortcut.description) }}</dt>
          <dd>
            <kbd class="rounded-sm border border-subtle bg-sunken px-1.5 py-0.5 text-2xs">
              {{ formatBinding(shortcut.keys) }}
            </kbd>
          </dd>
        </div>
      </dl>
    </div>
  </EDialog>
</template>
